import { View } from '../common/View';
import { Button } from '../common/Button';
import { SvgCheveronDown } from '../../icons/v1/CheveronDown';
import { Tooltip } from '../tooltips';
import { Menu } from '../common/Menu';
import { SheetCell } from '../table';
import { styles, theme } from '../../style';
import { reportBudget } from 'loot-core/src/client/queries';
import { makeAmountGrey } from '../budget/util';
import { amountToInteger, integerToCurrency } from 'loot-core/src/shared/util';
import { evalArithmetic } from 'loot-core/src/shared/arithmetic';
import React, { useState } from 'react';
import type { CategoryEntity } from 'loot-core/src/types/models';
import { CellEditing } from './types';
import * as monthUtils from 'loot-core/src/shared/months';
import { NamespaceContext } from '../spreadsheet/NamespaceContext';

export interface ICategoryMonthProps {
  category: CategoryEntity;
  month: string;
  monthIndex: number;
  editing: boolean;
  onEditCell: (cell: CellEditing) => void;
  onBudgetAction: (month: string, action: string, arg: unknown) => void;
}

export default function MonthCell({ category, month, monthIndex, editing, onEditCell, onBudgetAction }: ICategoryMonthProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [hover, setHover] = useState(false);

  return (
    <NamespaceContext.Provider
      key={monthIndex}
      value={monthUtils.sheetForMonth(month)}
    >
      <View
        style={{
          width: '100px',
          flex: 1,
          flexDirection: 'row'
        }}
        onMouseOverCapture={() => setHover(true)}
        onMouseLeave={() => {
          setHover(false);
        }}
      >
        {!editing && (hover || menuOpen) && (
          <View
            style={{
              flexShrink: 0,
              marginRight: 0,
              marginLeft: 3,
              justifyContent: 'center',
            }}
          >
            <Button
              type="bare"
              onClick={e => {
                e.stopPropagation();
                setMenuOpen(true);
              }}
              style={{
                padding: 3,
              }}
            >
              <SvgCheveronDown
                width={14}
                height={14}
                className="hover-visible"
                style={menuOpen && { opacity: 1 }}
              />
            </Button>
            {menuOpen && (
              <Tooltip
                ignoreBoundary={true}
                position="bottom-left"
                width={200}
                style={{ padding: 0 }}
                onClose={() => setMenuOpen(false)}
              >
                <Menu
                  onMenuSelect={type => {
                    onBudgetAction(month, type, { category: category.id });
                    setMenuOpen(false);
                  }}
                  items={[
                    {
                      name: 'copy-single-last',
                      text: 'Copy last month’s budget',
                    },
                    {
                      name: 'set-single-3-avg',
                      text: 'Set to 3 month average',
                    },
                    {
                      name: 'set-single-6-avg',
                      text: 'Set to 6 month average',
                    },
                    {
                      name: 'set-single-12-avg',
                      text: 'Set to yearly average',
                    },
                    {
                      name: 'apply-single-category-template',
                      text: 'Apply budget template',
                    },
                  ]}
                />
              </Tooltip>
            )}
          </View>
        )}
        <SheetCell
          name="budget"
          exposed={editing}
          focused={editing}
          width="flex"
          onExpose={() => onEditCell({ id: category.id, column: 'month', monthIndex: monthIndex })}
          style={{ ...(editing && { zIndex: 100 }), ...styles.tnum }}
          textAlign="right"
          valueStyle={{
            cursor: 'default',
            margin: 1,
            padding: '0 4px',
            borderRadius: 4,
            ':hover': {
              boxShadow: 'inset 0 0 0 1px ' + theme.mobileAccountShadow,
              backgroundColor: theme.tableBackground,
            },
          }}
          valueProps={{
            binding: reportBudget.catBudgeted(category.id),
            type: 'financial',
            getValueStyle: makeAmountGrey,
            formatExpr: expr => {
              return integerToCurrency(expr || 0);
            },
            unformatExpr: expr => {
              return amountToInteger(evalArithmetic(expr, 0));
            },
          }}
          inputProps={{
            onBlur: () => {
              onEditCell(null);
            },
            style: {
              backgroundColor: theme.tableBackground,
            },
          }}
          onSave={amount => {
            onBudgetAction(month, 'budget-amount', {
              category: category.id,
              amount,
            });
          }}
        />
      </View>
    </NamespaceContext.Provider>
  );
}
